<template>
  <div>
    <px-view-header title="Criar cotação" title-class="font-weight-bold"
                    :breadcrumbs="breadcrumbs">
      <template v-slot:before>
        <v-btn class="mb-4 mr-1" icon x-large color="primary" @click="onClickBack">
          <v-icon>
            mdi-arrow-left
          </v-icon>
        </v-btn>
      </template>
    </px-view-header>

    <div v-if="loadingForm">
      <px-circular-loading/>
    </div>

    <v-stepper class="elevation-1 rounded-card" v-if="!loadingForm" v-model="currentStep" vertical>

      <v-stepper-step :complete="currentStep > 1" step="1">
        Dados básicos
      </v-stepper-step>
      <v-stepper-content step="1">
        <px-cadastro-cotacao-dados-basicos is-from-requisicao
                                           :cotacao="cotacao"
                                           :campos-readonly="camposReadonly"
                                           :campos-obrigatorios="camposObrigatorios"
                                           @ready="next"
                                           @cancel="onClickCancelar"/>
      </v-stepper-content>

      <v-stepper-step :complete="currentStep > 2" step="2">Produtos</v-stepper-step>
      <v-stepper-content step="2">
        <px-cadastro-cotacao-produtos-table is-from-requisicao
                                            :cotacao="cotacao"
                                            :campos-readonly="camposReadonly"
                                            :campos-obrigatorios="camposObrigatorios"
                                            :erp-campos-integracao="erpCamposIntegracao"
                                            :hide-acoes="hideAcoes"
                                            @next="next"
                                            @previous="previous"
                                            @resetfornecedor="resetFornecedor"/>
      </v-stepper-content>

      <v-stepper-step :complete="currentStep > 3" step="3">Fornecedores</v-stepper-step>
      <v-stepper-content step="3">
        <px-cadastro-cotacao-fornecedores v-if="currentStep === 3"
                                          :cotacao="cotacao"
                                          :loading="loading"
                                          @previous="previous"
                                          @save-draft="saveDraft"
                                          @enviar-cotacao="enviarCotacao"
                                          @reset-fornecedor="resetFornecedor"/>
      </v-stepper-content>
    </v-stepper>
    <px-confirm-dialog ref="confirm"/>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { cloneDeep } from 'lodash';
import { ErpCamposIntegracao, SituacaoCotacao } from 'px-business-components';
import pxCadastroCotacaoDadosBasicos
  from '@/modules/cotacao/cadastro/CadastroCotacaoDadosBasicos.vue';
import pxCadastroCotacaoProdutosTable
  from '@/modules/cotacao/cadastro/produtos/CadastroCotacaoProdutosTable.vue';
import pxCadastroCotacaoFornecedores
  from '@/modules/cotacao/cadastro/fornecedores/CadastroCotacaoFornecedores.vue';
import CotacaoService from '@/modules/cotacao/cotacao-service';
import { deepNotEquals } from '@/utils/object-utils';
import { buildCadastrosAdicionaisPayload } from '@/utils/cadastros-adicionais-utils';
import TiposEntidadeCadastroAdicional from '@/enums/tipos-entidade-cadastro-adicional';
import EntidadeCamposCotacaoService from '@/modules/erp-campos/entidade-campos-cotacao-service';
import { createFromRequisicao } from '../cadastro/utils/cotacao-utils';

export default {
  data() {
    return {
      breadcrumbs: [
        { text: 'Gestão de compras' },
        {
          text: 'Cotações',
          to: '/cotacoes',
        },
        { text: 'Criar cotação' },
      ],
      idRequisicao: this.$route.query.idRequisicao,
      idsRequisicoes: this.$route.query.idsRequisicoes,
      isRascunho: this.$route.params.isRascunho,
      currentStep: 1,
      loadingForm: false,
      loading: false,
      cotacao: {},
      initialCotacao: {},
      camposReadonly: null,
      camposObrigatorios: null,
    };
  },
  computed: {
    ...mapGetters('entidade', ['erpCamposIntegracao']),
    confirmMessage() {
      return this.isRascunho ? 'Você deseja atualizar o rascunho?' : 'Você deseja salvar a cotação como rascunho?';
    },
    isFormDirty() {
      if (this.loadingForm) {
        return false;
      }
      return Object.keys(this.initialCotacao)
        .some(key => deepNotEquals(this.cotacao, this.initialCotacao, key));
    },
    hideAcoes() {
      return this.erpCamposIntegracao === ErpCamposIntegracao.keys.SENIOR_MEGA.key;
    },
  },
  methods: {
    setInitialCotacao() {
      this.initialCotacao = cloneDeep(this.cotacao);
    },
    onClickBack() {
      this.$refs.confirm.open(this.confirmMessage,
        'O rascunho ficara disponível na aba RASCUNHOS e TODAS na listagem de cotações.',
        { width: 450 })
        .then((confirm) => {
          if (!confirm) {
            this.$router.push({ name: 'main.cotacoes' });
            return;
          }
          this.saveDraft();
        });
    },
    onClickCancelar() {
      this.$router.push({ name: 'main.cotacoes' });
    },
    next() {
      this.currentStep = this.currentStep + 1;
    },
    previous() {
      this.currentStep = this.currentStep - 1;
    },
    buildCotacaoBody() {
      const cotacao = { ...this.cotacao };
      cotacao.id = undefined;
      cotacao.situacao = SituacaoCotacao.keys.ABERTA.key;
      cotacao.produtos = cotacao.produtos.map(produto => ({
        ...produto,
        situacao: SituacaoCotacao.keys.ABERTA.key,
        cadastrosAdicionais: buildCadastrosAdicionaisPayload(
          produto.cadastrosAdicionais,
          TiposEntidadeCadastroAdicional.keys.COTACAO_PRODUTO.key,
        ),
      }));
      return cotacao;
    },
    resetFornecedor() {
      this.cotacao.fornecedores = [];
    },
    enviarCotacao() {
      this.loading = true;
      CotacaoService.save(this.buildCotacaoBody())
        .then(() => {
          this.$toast('Cotação enviada com sucesso', { color: 'success' });
          this.$router.push({ name: 'main.cotacoes' });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getCotacao() {
      if (this.idRequisicao) {
        return this.getCotacaoFromRequisicao();
      }
      return this.getCotacaoFromRequisicoesProdutos();
    },
    getCotacaoFromRequisicoesProdutos() {
      const requisicoesProdutos = this.idsRequisicoes.split(',').map(id => ({
        id: Number(id),
      }));
      return CotacaoService.getFromRequisicoesProdutos(requisicoesProdutos)
        .then(({ data }) => {
          this.cotacao = createFromRequisicao(data, this.erpCamposIntegracao);
          this.setInitialCotacao();
        });
    },
    getCotacaoFromRequisicao() {
      return CotacaoService.getFromRequisicao(this.idRequisicao)
        .then(({ data }) => {
          this.cotacao = createFromRequisicao(data, this.erpCamposIntegracao);
          this.setInitialCotacao();
        });
    },
    buildCotacaoDraft() {
      const cotacao = { ...this.cotacao };
      cotacao.id = undefined;
      cotacao.situacao = SituacaoCotacao.keys.RASCUNHO.key;
      cotacao.projeto = cotacao.projeto || null;
      cotacao.centroCusto = cotacao.centroCusto || null;
      cotacao.formaPagamento = cotacao.formaPagamento || null;
      cotacao.prazoPagamento = cotacao.prazoPagamento || null;
      cotacao.produtos = cotacao.produtos
        .filter(produto => !!produto.unidadeMedida.id)
        .map(produto => ({
          ...produto,
          quantidade: produto.quantidade,
          situacao: SituacaoCotacao.keys.RASCUNHO.key,
          cadastrosAdicionais: buildCadastrosAdicionaisPayload(
            produto.cadastrosAdicionais,
            TiposEntidadeCadastroAdicional.keys.COTACAO_PRODUTO.key,
          ),
        }));
      return cotacao;
    },
    saveDraft() {
      this.loading = true;
      return CotacaoService.saveRascunho(this.buildCotacaoDraft())
        .then(() => {
          this.$toast('Rascunho salvo com sucesso', { color: 'success' });
          this.$router.push({ name: 'main.cotacoes' });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    beforeWindowUnload(e) {
      if (this.isFormDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    },
    getCamposReadonly() {
      return EntidadeCamposCotacaoService.getCamposReadonly(this.erpCamposIntegracao)
        .then((data) => {
          this.camposReadonly = data;
        });
    },
    getCamposObrigatorios() {
      return EntidadeCamposCotacaoService.getCamposObrigatorios(this.erpCamposIntegracao)
        .then((data) => {
          this.camposObrigatorios = data;
        });
    },
  },
  created() {
    this.loadingForm = true;

    Promise.all([
      this.getCotacao(),
      this.getCamposReadonly(),
      this.getCamposObrigatorios(),
    ])
      .finally(() => {
        this.setInitialCotacao();
        this.loadingForm = false;
      });
    window.addEventListener('beforeunload', this.beforeWindowUnload);
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.beforeWindowUnload);
  },
  components: {
    pxCadastroCotacaoFornecedores,
    pxCadastroCotacaoProdutosTable,
    pxCadastroCotacaoDadosBasicos,
  },
};
</script>
